import { Browser } from "../../core/browser";
import { Button } from "../button/button";
import { ChipsideComponent } from "../chipside-component";

export class Dropdown extends ChipsideComponent {
	static observedAttributes = ["value", "static", "icon-colour", "text", "icon", "size", "suffix-icon", "description", "label"];

	#GetInitialItems = null;
	#GetSearchedItems = null;

	#stopReset = false;
	#stopSearch = false;

	#searchTimeout = 500;

	#preventClose = false;
	#invalid = false;

	#previousValue = "";

	constructor() {
		super();

		this.label = this.getAttribute("label") ?? "";
		this.secondaryLabel = this.getAttribute("secondary-label") ?? "";
		this.value = this.getAttribute("value") ?? "";
		this.required = this.hasAttribute("required");
		this.initialValue = this.value;
		this.limitHeight = this.hasAttribute("limit-height");
		this.buttonID = this.getAttribute("button-id") ?? crypto.randomUUID().slice(0, 8);
		this.static = this.static ?? this.hasAttribute("static");
		this.default = this.getAttribute("default") ?? "";
		this.selectOnArrow = this.hasAttribute("select-on-arrow");
		this.helperText = this.getAttribute("helper-text") ?? "";
		this.suffixIcon = this.getAttribute("suffix-icon");
		this.orientation = this.getAttribute("orientation");
		this.readonly = this.getAttribute("readonly") ?? false;
		this.iconColour = this.getAttribute("icon-colour") ?? "";
		this.icon = this.getAttribute("icon") ?? "";
		this.localSearch = this.hasAttribute("local-search");
		this.size = this.getAttribute("size");
		this.description = this.getAttribute("description");
		this.text = this.getAttribute("text") ?? "";
		this.searchableLabel = this.getAttribute("searchable-label") ?? "";
		this.hiddenId = this.getAttribute("hidden-id");
		this.hiddenName = this.getAttribute("hidden-name");
		this.open = this.hasAttribute("open");
		this.variation = this.getAttribute("variation") ?? "";

		this.selectedItem = null;

		if (this.closest("form") && !this.hiddenName) {
			this.hiddenName = this.id;
		}

		this.selectedText = "";

		this.classList.add("dropdown__wrapper");
	}

	get GetInitialItems() {
		return this.#GetInitialItems;
	}

	set GetInitialItems(val) {
		this.#GetInitialItems = val;

		if (this.localSearch) {
			Promise.resolve(val?.() ?? []).then(items => {
				this.#GetInitialItems = () => items;

				this.GetSearchedItems = value => SearchArray(items, value, "textContent", 0);
				this.#searchTimeout = 0;

				this.#ResetInitialSearch(true);
			});
		} else {
			this.#ResetInitialSearch(true);
		}
	}

	get GetSearchedItems() {
		return this.#GetSearchedItems;
	}

	set GetSearchedItems(val) {
		this.#GetSearchedItems = val;
		this.#searchTimeout = 500;
	}

	async parsedCallback() {
		if (!this.static) {
			this.static = this.closest("chip-dropdownbutton, chip-splitbutton")?.querySelector("chip-dropdown") === this;
		}

		const wrapper = document.createElementWithContents("div", null, { className: this.static ? "" : "input-field" });

		if (this.static) {
			wrapper.classList.add("dropdown__static");
		}

		const button = document.createElementWithContents("button", null, {
			type: "button",
			id: this.buttonID,
			ariaExpanded: false
		});

		if (this.description) { button.ariaLabel = this.description; }

		if (!this.static && !this.closest("chip-splitbutton")) {
			if (!this.text && !this.value && !this.default) {
				this.value = this.querySelector("chip-dropdownitem")?.value;
			}

			let buttonContent = this.text;
			if (!buttonContent) { buttonContent = this.querySelector(`chip-dropdownitem[value='${this.value}']`)?.innerHTML.trim() ?? this.default; }

			button.appendChild(document.createElementWithContents("span", buttonContent || "&nbsp;"));
		}

		if (this.parentElement?.nodeName === "CHIP-INPUT") { button.tabIndex = -1; }
		if (this.icon) { button.prepend(document.createElementWithContents("i", null, { className: this.icon })); }
		if (this.size) { button.classList.add(`btn--${this.size.toLowerCase()}`); }

		let items = [...this.children].map(x => x.cloneNode(true));

		if (this.children.length) {
			this.#GetInitialItems = () => items;

			if (!this.GetSearchedItems) {
				this.GetSearchedItems = value => SearchArray(items, value, "textContent", 0);
				this.#searchTimeout = 0;
			}
		}

		if (this.label || this.secondaryLabel) {
			const label = document.createElementWithContents("label", this.label, {
				htmlFor: this.buttonID,
				className: "input-field__label"
			});

			if (this.secondaryLabel) { label.appendChild(document.createElementWithContents("span", this.secondaryLabel, { className: "input-field__label--secondary" })); }

			this.appendChild(label);
		}

		if (this.hiddenId || this.hiddenName) {
			const hiddenInput = document.createElementWithContents("input", null, {
				type: "hidden",
				value: this.value
			});

			if (this.hiddenId) { hiddenInput.id = this.hiddenId; }
			if (this.hiddenName) { hiddenInput.name = this.hiddenName; }

			wrapper.appendChild(hiddenInput);
		}

		wrapper.appendChild(button);

		const list = document.createElementWithContents("ul", null, {
			className: "dropdown__list"
		});

		if (this.limitHeight) { list.classList.add("limit-height"); }

		list.setAttribute("aria-labelledby", this.buttonID);

		this.#InitializeItemSet([...this.querySelectorAll(":scope > chip-dropdownitem, :scope > chip-dropdowndivider, :scope > chip-dropdowngroup, :scope > chip-dropdownheader")], list);

		let keys = "",
			timeout;

		if (this.variation === "searchable") {
			this.#BuildSearchableDropdown(list);
		}

		wrapper.appendChild(list);
		this.appendChild(wrapper);

		this.querySelector("button").onclick = this.#OnButtonClick;

		if (this.closest("chip-dropdownbutton")?.querySelector("chip-dropdown") === this) {
			this.#InitDropdownButton();
		}

		if (this.helperText) {
			this.appendChild(document.createElementWithContents("div", this.helperText, {
				className: "input-field__helper-text"
			}));
		}

		this.onkeydown = ev => {
			if (this.readonly) { return; }
			if (!["Enter", "Tab", "Shift"].includes(ev.key) && !this.classList.contains("dropdown--open")) { this.Open(); }

			switch (ev.key) {
				case "Escape":
					this.Close();
					this.closest("chip-input")?.focus();

					keys = "";
					break;
				case "ArrowDown":
					this.ArrowDown(ev);
					break;
				case "ArrowUp":
					this.ArrowUp(ev);
					break;
				default:
					if (timeout) {
						clearTimeout(timeout);
					}

					timeout = setTimeout(() => keys = "", 1000);

					keys += ev.key.toLowerCase();

					[...list.querySelectorAll("chip-dropdownitem")].find(x => x.textContent.trim().toLowerCase().indexOf(keys) === 0)?.parentElement.focus();

					if (ev.key === "spacebar") {
						ev.preventDefault();
					}
					break;
			}
		}
	}

	ArrowDown(ev) {
		ev.preventDefault();
		ev.stopPropagation();

		const items = [...this.querySelectorAll(".dropdown__list > li")].filter(x => !!x.querySelector("chip-dropdownitem")),
			currentIndex = items.indexOf(document.activeElement?.closest("li")),
			item = (items.at(currentIndex + 1) ?? items.at(0)).querySelector(":scope > button, :scope > a");

		if (item && this.selectOnArrow) {
			this.#SelectItem(item, true);
		}

		item?.focus();
	}

	ArrowUp(ev) {
		ev.preventDefault();
		ev.stopPropagation();

		const items = [...this.querySelectorAll(".dropdown__list > li")].filter(x => !!x.querySelector("chip-dropdownitem")),
			currentIndex = items.indexOf(document.activeElement?.closest("li")),
			item = items.at(currentIndex - 1)?.querySelector(":scope > button, :scope > a");

		if (item && this.selectOnArrow) {
			this.#SelectItem(item, true);
		}

		item?.focus();
	}

	#InitializeItemSet(set, list) {
		list ??= this.querySelector(".dropdown__list");

		if (list) {
			list.querySelectorAll(":scope > li:not(.dropdown--static-search)").forEach(x => x.remove());

			const groups = GroupBy(set, x => x.group ? x.group : "None"),
				keys = Object.keys(groups).sort(SortCaseInsensitive);

			if (keys.find(x => x !== "None")) {
				// Move the ungrouped section to the end
				if (keys.includes("None")) { keys.splice(keys.length - 1, 0, ...keys.splice(keys.indexOf("None"), 1)); }

				for (const key of keys) {
					this.AddItem(document.createElementWithContents("chip-dropdowngroup", null, { label: key }), list);

					for (const item of groups[key]) {
						this.AddItem(item, list);
					}
				}
			} else {
				for (const item of set) {
					this.AddItem(item, list);
				}
			}

			this.#Reposition(list);
		}
	}

	AddItem(item, list) {
		list ??= this.querySelector(".dropdown__list");

		if (list) {
			let listItem = document.createElement("li");

			switch (item.nodeName) {
				case "CHIP-DROPDOWNITEM":
					{
						let element = document.createElement("div");

						if (!item.detached) {
							element = !item.link
								? document.createElementWithContents("button", null, { type: "button" })
								: document.createElementWithContents("a", null, { href: Browser.ResolveRelativeURL(item.link) });
						}

						element.appendChild(item);

						if (item.disabled) { element.setAttribute("disabled", ""); }
						if (item.target) { element.target = item.target; }
						if (item.identifier) { element.id = item.identifier; }

						listItem.appendChild(element);

						if (!item.detached) {
							if (!this.static && item.getAttribute("value") === this.value) {
								element.classList.add("active");

								this.selectedItem = item;
								this.selectedText = item.textContent.trim();
							}

							element.onclick = ev => {
								ev.stopPropagation();

								if (this.static && !ev.target?.closest("chip-dropdownitem")) {
									element.querySelector("chip-dropdownitem")?.raiseEvent("click");
								}

								if (!this.readonly) {
									if (this.value === item.getAttribute("value") || !item.hasAttribute("value")) {
										this.raiseEvent("selectionchange");
									}

									this.value = item.getAttribute("value") ?? "";

									this.Close();
								}
							}
						}
						break;
					}
				case "CHIP-DROPDOWNHEADER":
				case "CHIP-DROPDOWNDIVIDER":
					listItem.appendChild(item);
					break;
				case "CHIP-DROPDOWNGROUP":
					listItem.classList.add("dropdown__group-header");
					listItem.appendChild(item);
					break;
			}

			list.appendChild(listItem);
		}
	}

	async #ResetInitialSearch(showPrompt, list) {
		list ??= this.querySelector(".dropdown__list");

		if (list) {
			list.querySelectorAll(":scope > li:not(.dropdown--static-search)").forEach(x => x.remove());
			list.querySelector(".dropdown--searchable__prompt--initial")?.remove();

			list.querySelector(".pv-loading-container")?.classList.remove("d-none");
			list.querySelectorAll("chip-emptyprompt").forEach(x => x.classList.add("d-none"));

			const initialResults = this.GetInitialItems ? await this.GetInitialItems() : [];

			list.querySelector(".pv-loading-container")?.classList.add("d-none");

			if (this.#stopReset) {
				this.#stopReset = false;
				return;
			}

			if (initialResults.length) {
				this.#InitializeItemSet(initialResults, list);

				if (this.value) {
					this.#SelectValue(this.value, true);
				}
			} else if (showPrompt) {
				list.querySelector(".dropdown--searchable__prompt--empty")?.remove();
				this.#AddNoSearchPrompt(list);
			}
		}
	}

	#CancelReset() {
		this.#stopReset = true;
		this.#stopSearch = false;
	}

	#CancelSearch() {
		this.#stopSearch = true;
		this.#stopReset = false;
	}

	RefreshItems(list) {
		list ??= this.querySelector(".dropdown__list");

		if (list) {
			this.#CancelSearch();
			this.#ResetInitialSearch(true, list);
		}
	}

	#AddEmptySearchPrompt(list) {
		list ??= this.querySelector(".dropdown__list");

		list?.querySelector(".dropdown--searchable__wrapper")?.appendChild(document.createElementWithContents("chip-emptyprompt", "Try adjusting your search and we'll have another look", {
			size: "sm",
			heading: "No results found",
			icon: "fa-file-alt",
			className: "dropdown--searchable__prompt--empty"
		}));
	}

	#AddNoSearchPrompt(list) {
		list ??= this.querySelector(".dropdown__list");

		list?.querySelector(".dropdown--searchable__wrapper")?.appendChild(document.createElementWithContents("chip-emptyprompt", "Before we can show you anything, try making a search", {
			size: "sm",
			heading: "Make a search",
			icon: "fa-search",
			className: "dropdown--searchable__prompt--initial"
		}));
	}

	async Search(value, list) {
		list ??= this.querySelector(".dropdown__list");

		if (list) {
			list.querySelectorAll(":scope > li:not(.dropdown--static-search)").forEach(x => x.remove());

			if (value.trim() !== "") {
				list.querySelector(".dropdown--searchable__prompt--initial")?.remove();
				this.#CancelReset();

				list.querySelector(".pv-loading-container")?.classList.remove("d-none");
				list.querySelectorAll("chip-emptyprompt").forEach(x => x.classList.add("d-none"));

				const results = this.GetSearchedItems ? await this.GetSearchedItems(value) : [];

				list.querySelector(".pv-loading-container")?.classList.add("d-none");

				if (this.#stopSearch) {
					this.#stopSearch = false;
					return;
				}

				this.#InitializeItemSet(results, list);

				if (this.value && this.#GetItemFromValue(this.value)) {
					this.#SelectValue(this.value, true);
				}

				list.querySelector(".dropdown--searchable__prompt--empty")?.remove();

				if (!results.length) {
					this.#ResetInitialSearch(false, list);

					if (list.children.length === 1) {
						this.#AddEmptySearchPrompt(list);
					}
				}
			} else {
				this.#AddNoSearchPrompt(list);
				this.RefreshItems(list);
			}
		}
	}

	#BuildSearchableDropdown(list) {
		let timeout;

		list.prepend(document.createElementWithContents("li",
			`
				<div class="dropdown--searchable__wrapper">
					<chip-input placeholder="Search" icon-style="integrated" prefix-icon="far fa-search"></chip-input>

					<div class="pv-loading-container d-none mt-xxl mb-xl"><div class="pv-loading-icon"></div></div>
				</div>
			`.trim(), { className: "dropdown--static-search" }));

		this.#ResetInitialSearch(true, list);

		const input = list.querySelector("chip-input");

		input.onkeydown = ev => {
			ev.stopImmediatePropagation();
			ev.stopPropagation();

			switch (ev.key) {
				case "Enter":
					clearTimeout(timeout);

					this.Search(input.value, list);
					break;
				case "Escape":
					this.Close();
					break;
				case "ArrowDown":
					this.ArrowDown(ev);
					break;
				case "ArrowUp":
					this.ArrowUp(ev);
					break;
			}
		}

		input.oninput = () => {
			if (timeout) {
				clearTimeout(timeout);
			}

			timeout = setTimeout(() => {
				this.Search(input.value, list);
			}, this.#searchTimeout);
		}
	}

	#InitDropdownButton() {
		const button = this.querySelector(":scope > div > button"),
			dropdownButton = this.closest("chip-dropdownbutton");

		button.append(...[...dropdownButton.childNodes ?? []].filter(x => !x.matches?.("chip-dropdown")));
		button.classList.add("btn--base", Button.ClassFromVariation(dropdownButton.variation));

		if (dropdownButton.buttonStyle) {
			button.classList.add(`btn--${dropdownButton.buttonStyle.toLowerCase()}`);
		}
	}

	#OnButtonClick(event) {
		event.preventDefault();
		const dropdown = event.target?.closest("chip-dropdown");

		if (dropdown.open || !dropdown.querySelector(".dropdown__list > *")) {
			dropdown?.Close();
		} else {
			dropdown?.Open();
		}
	}

	#Reposition(list) {
		list ??= this.querySelector(".dropdown__list");

		if (list?.parentElement) {
			this.classList.add("dropdown--open");

			const containerDimensions = list.parentElement.getBoundingClientRect(),
				parent = getScrollParent(list);

			const comparatorDimensions = document.body !== parent ? parent.getBoundingClientRect() : {
				bottom: window.innerHeight,
				right: window.innerWidth,
				top: 0
			},
				{ height, top, width, left } = list.getBoundingClientRect();

			if (!this.open) {
				this.classList.remove("dropdown--open");
			}

			if (height + top > comparatorDimensions.bottom && comparatorDimensions.top <= containerDimensions.top - height - containerDimensions.height) {
				list.style.top = `calc(-${height}px - (var(--block-size-sm) * 2))`;
			}

			if (width + left > comparatorDimensions.right) {
				list.style.left = `-${width - containerDimensions.width}px`;
			}
		}
	}

	Open() {
		this.#Reposition();
		this.classList.add("dropdown--open");

		this.querySelector(".dropdown__list chip-input")?.select();
		this.raiseEvent("opened");

		this.open = true;
	}

	Close() {
		const list = this.querySelector(".dropdown__list");

		if (list) {
			this.removeClass("dropdown--open");
			list.removeAttribute("style");
		}

		this.open = false;
	}

	#SelectItem(item, preventClose) {
		const isIconPicker = !!this.closest("chip-iconpicker");

		if (item) {
			this.#preventClose = preventClose;

			const valueChanged = this.#previousValue?.toString() !== item.firstElementChild?.value?.toString() && this.selectedItem !== item.firstElementChild;

			this.selectedItem = item.firstElementChild;
			this.selectedText = item.textContent.trim();

			this.value = item.firstElementChild?.value?.toString();

			if (this.hiddenId || this.hiddenName) {
				this.querySelector("input[type='hidden']").value = this.value;
			}

			if (!isIconPicker && !this.static) {
				this.querySelector(":scope > div > button > span").innerHTML = item.querySelector(":scope > chip-dropdownitem")?.innerHTML ?? this.default;
			}

			if (!this.static || isIconPicker) {
				this.querySelector(".active")?.removeClass("active");
				item.classList.add("active");
			}

			this.raiseEvent("selectionchange");
			if (!preventClose) { this.Close(); }
			if (valueChanged) { this.raiseEvent("change"); }
		} else if (!isIconPicker && !this.static) {
			this.querySelector(":scope > div > button > span").textContent = this.default;
		}

		if (this.#invalid) {
			this.reportValidity();
			this.closest("chip-form")?.checkValidity();
		}
	}

	attributeChanged(name, oldValue, newValue) {
		switch (name) {
			case "value":
				this.#previousValue = oldValue;

				this.#SelectValue(newValue, this.#preventClose);
				this.#preventClose = false;
				break;
			case "label":
				{
					const label = this.querySelector(".input-field__label");

					if (label) {
						label.textContent = newValue;
					}
				}
				break;
			case "static":
				{
					const container = this.querySelector("div");

					if (!this.IsAttributeValueTrue(newValue)) {
						container.classList.remove("dropdown__static");
						container.classList.add("input-field");

						if (!this.querySelector(":scope > div > button > span")) {
							this.querySelector(":scope > div > button").appendChild(document.createElementWithContents("span", "&nbsp"));
						}
					} else {
						container.classList.add("dropdown__static");
						container.classList.remove("input-field");

						this.querySelector(":scope > div > button > span")?.remove();

						this.querySelectorAll(".active").forEach(x => x.removeClass("active"));
					}
				}
				break;
			case "description":
				{
					const button = this.querySelector(":scope > div > button");

					if (button) {
						button.ariaLabel = newValue;
					}
				}
				break;
			case "icon-colour":
				if (!newValue) {
					this.querySelector(".input-field > button > i").removeAttribute("style");
				} else {
					this.querySelector(".input-field > button > i").style.color = newValue;
				}
				break;
			case "text":
				this.querySelector(":scope > div > button > span").textContent = newValue;
				break;
			case "size":
				{
					const button = this.querySelector(":scope > div > button");

					button?.classList.remove(`btn--${this.size?.toLowerCase()}`);
					button?.classList.add(`btn--${newValue.toLowerCase()}`);
				}
				break;
			case "icon": {
				const icon = this.querySelector(":scope > div > button > i");

				if (icon) {
					icon.className = newValue;
				} else {
					this.querySelector(":scope > div > button")?.prepend(document.createElementWithContents("i", null, { className: newValue }));
				}
			}
				break;
			case "suffix-icon": {
				const icon = this.querySelector(":scope > div > button > i:last-child:not(:first-child)");

				if (icon) {
					icon.className = newValue;
				} else {
					this.querySelector(":scope > div > button")?.appendChild(document.createElementWithContents("i", null, { className: newValue }));
				}
			}
				break;
		}
	}

	checkValidity() {
		return !this.required || this.selectedItem;
	}

	reportValidity() {
		let message = "";

		if (this.required && !this.selectedItem) { message = Localizer.DROPDOWN_REQUIRED; }

		this.querySelector(".input-field__validation-error")?.remove();

		if (message) {
			this.querySelector(".input-field")?.classList.add("input-field__invalid");
			this.appendChild(document.createElementWithContents("div", message, { className: "input-field__validation-error" }));
		} else {
			this.querySelector(".input-field")?.classList.remove("input-field__invalid");
		}

		this.#invalid = !!message;

		return !message;
	}

	#GetItemFromValue(value) {
		return this.querySelector(`chip-dropdownitem[value='${value}']`)
			?? [...this.querySelectorAll("chip-dropdownitem")].find(x => x.value?.toString() === value?.toString() || x.getAttribute("value")?.toString() === value?.toString());
	}

	#SelectValue(value, preventClose) {
		const target = this.#GetItemFromValue(value);

		if (target) {
			this.#SelectItem(target.closest("button, a"), preventClose);
		} else if (!this.static && this.default) {
			this.querySelector(":scope > div > button > span").textContent = this.default;
		}
	}
}